import { useCometMcpServers } from '#pplx/frontend/apps/ask/comet-browser/hooks/useCometMcpServers.js';
import { useOnConnectorDisconnect } from '#pplx/frontend/apps/ask/connectors/hooks/useConnectorDisconnectEvents.js';
import { useNativeBridgeActions } from '#pplx/frontend/apps/ask/native-bridges/store.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { getCometMcpServerName, getCometMcpSourceType, isCometMcpSourceType, } from '#pplx/frontend/apps/ask/sources/types/cometMcpSourceTypes.js';
import { SOURCE_SELECTION_CHANGE_CHANNEL } from '#pplx/frontend/components/utils/consts.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
/**
 * This hook manages how sources are loaded and selected.
 * It also broadcasts changes to other windows in the Windows app.
 */
export function useSharedSourceSelection() {
    const { sources, setSources } = useThreadConfig();
    const { publishIpcMessage, subscribeToIpcMessage } = useNativeBridgeActions();
    // Generate a unique window ID for this instance
    const windowIdRef = useRef(Math.random().toString(36).substring(7));
    const toggleSource = useCallback((source) => {
        let finalSources = [];
        // This branch is for removing a source
        if (sources?.includes(source)) {
            finalSources = sources.filter((prevSource) => prevSource !== source);
            if (source === 'web') {
                // Exception: If we try to remove web and crunchbase is selected, just remove both
                if (sources.includes('crunchbase')) {
                    finalSources = sources.filter((prevSource) => prevSource !== 'crunchbase' && prevSource !== 'web');
                }
            }
        }
        else {
            // This branch is for adding a source
            finalSources = [...(sources || [])];
            // Exception: If adding crunchbase, ensure web is included
            if (source === 'crunchbase' && !finalSources.includes('web')) {
                finalSources.push('web');
            }
            finalSources.push(source);
        }
        setSources(finalSources);
        // Broadcast changes to other windows if in Windows app
        publishIpcMessage(SOURCE_SELECTION_CHANGE_CHANNEL, {
            type: SOURCE_SELECTION_CHANGE_CHANNEL,
            sources: finalSources,
            windowId: windowIdRef.current,
        });
        return finalSources;
    }, [setSources, sources, publishIpcMessage]);
    const addSource = useCallback((source) => {
        if (!sources?.includes(source)) {
            toggleSource(source);
        }
    }, [sources, toggleSource]);
    const removeSource = useCallback((source) => {
        if (sources?.includes(source)) {
            toggleSource(source);
        }
    }, [sources, toggleSource]);
    const hasSource = useCallback((source) => sources?.includes(source) ?? false, [sources]);
    const overwriteSources = useCallback((sources) => {
        const finalSources = [...sources];
        // Exception: If adding crunchbase, ensure web is included
        if (finalSources.includes('crunchbase') && !finalSources.includes('web')) {
            finalSources.push('web');
        }
        setSources(finalSources);
        // Broadcast changes to other windows if in Windows app
        publishIpcMessage(SOURCE_SELECTION_CHANGE_CHANNEL, {
            type: SOURCE_SELECTION_CHANGE_CHANNEL,
            sources: finalSources,
            windowId: windowIdRef.current,
        });
    }, [setSources, publishIpcMessage]);
    // Set up IPC subscription for receiving updates
    useEffect(() => {
        return subscribeToIpcMessage(SOURCE_SELECTION_CHANGE_CHANNEL, (data) => {
            // Ignore messages from this window
            if (data.type === SOURCE_SELECTION_CHANGE_CHANNEL &&
                data.windowId !== windowIdRef.current) {
                setSources(data.sources);
            }
        });
    }, [subscribeToIpcMessage, setSources, sources]);
    // when a connector is explicitly disconnected, remove its source from the selection
    useOnConnectorDisconnect((event) => {
        removeSource(event.sourceId);
    });
    return useMemo(() => ({
        sources,
        toggleSource,
        addSource,
        removeSource,
        hasSource,
        overwriteSources,
    }), [sources, overwriteSources, toggleSource, addSource, removeSource, hasSource]);
}
/**
 * Hook that manages globally shared available sources, combining both Perplexity sources
 * and Comet MCP sources.
 *
 * This hook integrates with:
 * - useSharedSourceSelection for Perplexity sources (web, scholar, social, etc.)
 * - useCometMcpServers for Comet MCP sources
 *
 * @returns Object containing combined sources array and setSources function
 */
export function useGlobalAvailableSources() {
    const { sources: globalSources, overwriteSources: overwriteGlobalSources } = useSharedSourceSelection();
    const { cometMcpSources, cometMcpDisabledServers, setCometMcpSources: setGlobalCometMcpSources, } = useCometMcpServers();
    // Combine Perplexity sources with enabled Comet MCP sources
    const sources = useMemo(() => {
        const mcpSources = Object.keys(cometMcpSources)
            .filter((serverName) => !cometMcpDisabledServers?.has(serverName))
            .map((serverName) => getCometMcpSourceType(serverName));
        return [...globalSources, ...mcpSources];
    }, [globalSources, cometMcpSources, cometMcpDisabledServers]);
    // Handle sources change by splitting into Perplexity and MCP sources
    const setSources = useCallback((newSources) => {
        const mcpSources = newSources.filter(isCometMcpSourceType);
        const perplexitySources = newSources.filter((source) => !isCometMcpSourceType(source));
        setGlobalCometMcpSources(mcpSources.map((source) => getCometMcpServerName(source)));
        overwriteGlobalSources(perplexitySources);
    }, [overwriteGlobalSources, setGlobalCometMcpSources]);
    const addSource = useCallback((sourceType) => {
        if (!sources.includes(sourceType)) {
            setSources([...sources, sourceType]);
        }
    }, [sources, setSources]);
    const removeSource = useCallback((sourceType) => {
        if (sources.includes(sourceType)) {
            setSources(sources.filter((source) => source !== sourceType));
        }
    }, [sources, setSources]);
    return {
        sources,
        setSources,
        addSource,
        removeSource,
    };
}
//# sourceMappingURL=useSharedSourceSelection.js.map